/**
 * =============================================================================
 *  DU LIEU CUA THANH CONG CU - TACH KHOI JSX DE KIEM DUOC
 * =============================================================================
 *
 *  Thanh cong cu gio co hai menu tha xuong ("Trang" va "Chèn"). Mot cong cu nam
 *  trong menu van phai giu nguyen phim tat cua no, va do la cho de mat nhat:
 *  chuyen mot nut vao menu roi quen mat dong `case 'r':` trong bo bat phim thi
 *  khong ai phat hien ra cho den khi co nguoi go R.
 *
 *  Nen o day chi co MOT bang duy nhat:
 *
 *    - `TOOL_SHORTCUTS` la nguon su that cho bo bat phim toan cuc trong App.tsx.
 *    - Cung bang do sinh ra chuoi phim hien trong tung muc menu.
 *    - Test doi chieu: moi cong cu co phim tat PHAI voi toi duoc bang mot nut
 *      hoac mot muc menu, va nguoc lai.
 *
 *  Neu hai thu do troi khoi nhau thi hoac co mot phim tat am tham (khong nut
 *  nao the hien no), hoac co mot muc menu quang cao mot phim khong lam gi ca.
 */
import type { ToolId } from '../state';

export interface ToolSpec {
  id: ToolId;
  label: string;
  hint: string;
  icon: string;
  /** Phim tat mot ky tu, chu THUONG. Bo qua = cong cu nay khong co phim tat. */
  shortcut?: string;
}

/**
 * HAI CONG CU O LAI NGOAI MENU.
 *
 * Chung la CHE DO chu khong phai thao tac chen, va la hai thu duoc dung nhieu
 * nhat: giau chung sau mot cu bam nua la bat nguoi dung tra gia o duong di
 * thuong xuyen nhat de doi lay mot thanh cong cu gon hon.
 */
export const DIRECT_TOOLS: readonly ToolSpec[] = [
  { id: 'select', label: 'Chọn', hint: 'Chọn văn bản và đối tượng', icon: '⌖', shortcut: 'v' },
  { id: 'text', label: 'Sửa chữ', hint: 'Sửa chữ có sẵn trong tài liệu', icon: 'T', shortcut: 't' },
];

/**
 * MENU "CHÈN" - moi thu SINH RA mot doi tuong moi tren trang.
 *
 * Danh sach PHANG chu khong long nhau. Bon hinh khoi truoc day nam trong mot
 * menu con cua chinh no; gop tiep vao "Chèn" se thanh menu trong menu, va menu
 * long nhau tren mot thanh cong cu la thu vua kho bam bang chuot vua kho di
 * chuyen bang ban phim. `group` chi de ve mot duong ke ngan, khong phai mot
 * tang dieu huong.
 */
export interface InsertItem extends ToolSpec {
  group: 'text' | 'shape' | 'media';
}

export const INSERT_ITEMS: readonly InsertItem[] = [
  { id: 'freetext', group: 'text', label: 'Hộp chữ', hint: 'Thêm một hộp chữ mới lên trang', icon: '⊞', shortcut: 'x' },
  { id: 'note', group: 'text', label: 'Ghi chú', hint: 'Dán một ghi chú đọc được lên trang', icon: '❏', shortcut: 'n' },
  { id: 'square', group: 'shape', label: 'Hình chữ nhật', hint: 'Kéo để vẽ khung chữ nhật', icon: '▭', shortcut: 'r' },
  { id: 'circle', group: 'shape', label: 'Hình bầu dục', hint: 'Kéo để vẽ hình bầu dục', icon: '◯', shortcut: 'e' },
  { id: 'line', group: 'shape', label: 'Đường thẳng', hint: 'Kéo để vẽ đường thẳng', icon: '╱', shortcut: 'l' },
  { id: 'arrow', group: 'shape', label: 'Mũi tên', hint: 'Kéo để vẽ mũi tên', icon: '↗', shortcut: 'a' },
  // Chen anh KHONG co phim tat, va co chu: no mo mot hop thoai chon tep cua he
  // dieu hanh. Mot phim tat mot ky tu bat ra hop thoai tep la thu nguoi dung
  // cham phai khi dang go, chu khong phai thu ho tim.
  { id: 'image', group: 'media', label: 'Chèn ảnh', hint: 'Chèn một ảnh PNG hoặc JPEG lên trang', icon: '▣' },
  { id: 'ink', group: 'media', label: 'Vẽ tay', hint: 'Vẽ tự do lên trang', icon: '✎', shortcut: 'd' },
];

/**
 * BANG PHIM TAT CONG CU - nguon su that cho bo bat phim toan cuc.
 *
 * Sinh ra tu chinh cac muc o tren, nen khong the co phim tat nao khong hien
 * duoc tren thanh cong cu.
 */
export const TOOL_SHORTCUTS: Readonly<Record<string, ToolId>> = Object.freeze(
  Object.fromEntries(
    [...DIRECT_TOOLS, ...INSERT_ITEMS]
      .filter((t): t is ToolSpec & { shortcut: string } => typeof t.shortcut === 'string')
      .map((t) => [t.shortcut, t.id]),
  ),
);

/** "Kéo để vẽ mũi tên (A)" - nhan day du cho `title` va cho muc menu. */
export function shortcutLabel(spec: ToolSpec): string {
  return spec.shortcut ? spec.shortcut.toUpperCase() : '';
}

export function hintWithShortcut(spec: ToolSpec): string {
  const key = shortcutLabel(spec);
  return key ? `${spec.hint} (${key})` : spec.hint;
}

/**
 * Muc dang duoc bat trong menu "Chèn", hoac null.
 *
 * Nut mo menu PHAI hien cai nay. Mot cong cu dang cam ma khong nhin thay la
 * duong dan thang toi viec ve mot hinh chu nhat trong khi dinh chon mot doi
 * tuong - nguoi dung bam len trang va bat ngo tao ra mot vat the moi.
 */
export function activeInsertItem(tool: ToolId): InsertItem | null {
  return INSERT_ITEMS.find((item) => item.id === tool) ?? null;
}

// ---------------------------------------------------------------------------
// Di chuyen trong menu bang ban phim
// ---------------------------------------------------------------------------

/**
 * Chi so ke tiep khi go mot phim dieu huong trong menu dang mo.
 *
 * Tra ve `null` nghia la "phim nay khong phai viec cua menu" - de nguyen cho
 * trinh duyet. Danh sach QUAN VONG: tu muc cuoi bam xuong thi ve muc dau. Menu
 * ngan (5-8 muc) ma dung lai o hai dau se lam nguoi dung tuong menu bi treo.
 */
export function nextMenuIndex(current: number, key: string, count: number): number | null {
  if (count <= 0) return null;
  switch (key) {
    case 'ArrowDown':
      return current < 0 ? 0 : (current + 1) % count;
    case 'ArrowUp':
      return current <= 0 ? count - 1 : current - 1;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/** Phim mo menu tu nut kich hoat. Enter, Space va mui ten xuong - nhu ARIA. */
export function opensMenu(key: string): boolean {
  return key === 'Enter' || key === ' ' || key === 'ArrowDown';
}

// ---------------------------------------------------------------------------
// Cua chan cua cac thao tac trang tren thanh cong cu
// ---------------------------------------------------------------------------

/**
 * MOT NUT BI KHOA PHAI NOI VI SAO.
 *
 * Day la ly do ham nay ton tai thay vi vai bieu thuc `disabled={...}` rai rac:
 * "xoay trai bi mo" va "xoay trai bi khoa vi chua chon trang nao" khac nhau
 * mot troi mot vuc doi voi nguoi dung, nhung trong JSX chung chi khac nhau mot
 * dau `!`. Gom lai mot cho thi khong the co nut nao bi khoa cam lang.
 *
 * Thu tu kiem la co chu, tu xa den gan: chua co tai lieu thi noi the truoc,
 * chu khong bao "chưa chọn trang nào" khi con chua mo tep nao.
 */
export type PageAction = 'rotate' | 'delete' | 'extract' | 'insertBlank' | 'split' | 'merge';

export interface PageActionGate {
  /** Loi hien tai lam duoc thao tac trang khong. */
  capable: boolean;
  /** Dang chay mot thao tac dai (trich xuat, tach, doc tep ghep). */
  busy: boolean;
  totalPages: number;
  selectedCount: number;
}

export interface PageActionState {
  disabled: boolean;
  /** Luon co chuoi: khi bat duoc thi day la mo ta thao tac, khi khoa thi la ly do. */
  reason: string;
}

const NEEDS_SELECTION: ReadonlySet<PageAction> = new Set<PageAction>([
  'rotate',
  'delete',
  'extract',
]);

export function pageActionState(action: PageAction, gate: PageActionGate): PageActionState {
  if (!gate.capable) {
    return { disabled: true, reason: 'Lõi hiện tại chưa làm được thao tác trang.' };
  }
  if (gate.totalPages === 0) {
    return { disabled: true, reason: 'Chưa mở tài liệu nào.' };
  }
  if (gate.busy) {
    return { disabled: true, reason: 'Đang chạy một thao tác trang khác — chờ một nhịp.' };
  }
  if (NEEDS_SELECTION.has(action) && gate.selectedCount === 0) {
    return {
      disabled: true,
      reason: 'Chưa chọn trang nào. Bấm một trang trong thanh bên (giữ Ctrl để chọn thêm).',
    };
  }
  // Xoa het trang la khong hop le. Bao TRUOC khi bam, khong phai sau.
  if (action === 'delete' && gate.selectedCount >= gate.totalPages) {
    return {
      disabled: true,
      reason: 'Không xóa được toàn bộ trang: PDF phải có ít nhất một trang.',
    };
  }
  return { disabled: false, reason: describePageAction(action, gate) };
}

function describePageAction(action: PageAction, gate: PageActionGate): string {
  const n = gate.selectedCount;
  switch (action) {
    case 'rotate':
      return `Xoay ${n} trang đang chọn.`;
    case 'delete':
      return `Xóa ${n} trang đang chọn.`;
    case 'extract':
      return `Trích ${n} trang đang chọn ra một tệp PDF riêng.`;
    case 'insertBlank':
      return n > 0
        ? 'Chèn một trang trắng ngay sau trang đang chọn.'
        : 'Chèn một trang trắng vào cuối tài liệu.';
    case 'split':
      return `Tách thành ${gate.totalPages} tệp, mỗi tệp một trang.`;
    case 'merge':
      return 'Ghép thêm một hoặc nhiều tệp PDF vào cuối tài liệu.';
  }
}
